// src/pages/TermsAndConditionsPage.jsx
import React from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

import AnimatedSection, { fadeInUp } from '../components/AnimatedSection'; 
import ContactForm from '../components/ContactForm'; 


const AnimatedTitle = ({ title }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const wordVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12,
      },
    },
  };

  return (
    <motion.h2
      ref={ref}
      className="text-2xl md:text-3xl font-bold text-gray-900"
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      aria-label={title}
    >
      {title.split(" ").map((word, index) => (
        <motion.span
          key={index}
          variants={wordVariants}
          className="inline-block mr-[0.25em]"
        >
          {word}
        </motion.span>
      ))}
    </motion.h2>
  );
};

const PolicySection = ({ title, children }) => {
  return (
    <div className="md:grid md:grid-cols-3 md:gap-12 border-t border-gray-200 pt-8">
      <div className="md:col-span-1 mb-4 md:mb-0">
        <AnimatedTitle title={title} />
      </div>
      <div className="md:col-span-2 text-lg leading-relaxed text-gray-700 space-y-4 border-l border-gray-200 pl-8">
        {children}
      </div>
    </div>
  );
};

function TermsAndConditionsPage() {
  return (
    <>
      {/* Hero Section for the Page Title (Left-Aligned) */}
      <AnimatedSection
        animationVariants={fadeInUp}
        className="bg-black text-white pt-32 pb-16 md:pt-48 md:pb-24"
      >
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-left">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight">
              Terms and Conditions
            </h1>
            <p className="mt-4 text-lg text-gray-300 max-w-3xl">
              At ZenithBlox, our mission is to empower financial institutions worldwide with seamless blockchain integration. We strive to provide cutting-edge solutions that drive innovation, increase operational efficiency, and foster trust within the global financial ecosystem.
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* Main Content Section for the Terms Text */}
      <AnimatedSection className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="space-y-16">

            <PolicySection title="Intellectual Property">
                <p>All content published and made available on our Site is the property of ZenithBlox Inc. and the Site's creators. This includes, but is not limited to images, text, logos, documents, downloadable files, and anything that contributes to the composition of our Site.</p>
            </PolicySection>

            <PolicySection title="Accounts">
                <p>When you create an account on our Site, you agree to the following:</p>
                <ol className="list-decimal pl-6 space-y-2">
                    <li>You are solely responsible for your account and the security and privacy of your account, including passwords or sensitive information attached to that account; and</li>
                    <li>All personal information you provide to us through your account is up-to-date, accurate, and truthful, and that you will update your personal information if it changes.</li>
                </ol>
                <p>We reserve the right to suspend or terminate your account if you are using our Site illegally or if you violate these Terms and Conditions.</p>
            </PolicySection>

            <PolicySection title="Limitation of Liability">
                <p>ZenithBlox Inc. and our directors, officers, agents, employees, subsidiaries, and affiliates will not be liable for any actions, claims, losses, damages, liabilities, and expenses, including legal fees, from your use of the Site.</p>
            </PolicySection>
            
            <PolicySection title="Indemnity">
                <p>Except where prohibited by law, by using this Site you indemnify and hold harmless ZenithBlox Inc. and our directors, officers, agents, employees, subsidiaries, and affiliates from any actions, claims, losses, damages, liabilities, and expenses, including legal fees, arising out of your use of our Site or your violation of these Terms and Conditions.</p>
            </PolicySection>

            <PolicySection title="Applicable Law">
                <p>These Terms and Conditions are governed by the laws of the Province of Ontario.</p>
            </PolicySection>

            <PolicySection title="Dispute Resolution">
                <p>Subject to any exceptions specified in these Terms and Conditions, if you and ZenithBlox Inc. are unable to resolve any dispute through informal discussion, then you and ZenithBlox Inc. agree to submit the issue first before a non-binding mediator and to an arbitrator in the event that mediation fails. The decision of the arbitrator will be final and binding. Any mediator or arbitrator must be a neutral party acceptable to both you and ZenithBlox Inc.</p>
                <p>The costs of any mediation or arbitration will be shared equally between you and ZenithBlox Inc. Notwithstanding any other provision in these Terms and Conditions, you and ZenithBlox Inc. agree that you both retain the right to bring an action in small claims court.</p>
            </PolicySection>

            <PolicySection title="Severability">
                <p>If at any time any of the provisions set forth in these Terms and Conditions are found to be inconsistent or invalid under applicable laws, those provisions will be deemed void and will be removed from these Terms and Conditions. All other provisions will not be affected by the removal, and the rest of these Terms and Conditions will still be considered valid.</p>
            </PolicySection>

            <PolicySection title="Changes">
                <p>These Terms and Conditions may be amended from time to time to maintain compliance with the law and to reflect any changes to the way we operate our Site and the way we expect users to behave on our Site. We will notify users by email of changes to these Terms and Conditions or post a notice on our Site.</p>
            </PolicySection>

            <PolicySection title="Contact Details">
              <p>Please contact us if you have any questions or concerns. Our contact details are as follows:</p>
              <p><a href="mailto:contact@zenithblox.network" className="font-semibold text-purple-700 hover:text-purple-800 transition-colors">contact@zenithblox.network</a></p>
              <p>You can also contact us through the feedback form available on our Site.</p>
            </PolicySection>

          </div>
        </div>
      </AnimatedSection>

      {/* Consistent Contact Form CTA at the bottom of the page */}
      <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} threshold={0.05}>
        <ContactForm />
      </AnimatedSection>
    </>
  );
}

export default TermsAndConditionsPage;